import { zodResolver } from '@hookform/resolvers/zod'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { DateFormatter } from '@repo/toolkit/date'
import { toFullName } from '@repo/toolkit/to-full-name'
import { FileTypeEnum } from '@repo/toolkit/types'
import Badge from '@repo/ui/badge'
import UploadFileInput from '@repo/ui/file-input'
import { DownloadFile } from '@repo/ui/icons'
import { useQuery } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'

import { getEnrollments } from '../../../api/enrollments/get-enrollments'
import { getUsers } from '../../../api/user/get-users'
import useAuth from '../../../lib/hooks/use-auth'
import {
  EventTypeEnum,
  eventTypeEnumMapping
} from '../../../lib/types/enums/center-event/event-type.enum'
import { RoleEnum } from '../../../lib/types/enums/users/role.enum'
import { CenterEventInput } from '../../../lib/types/models/center-event/center-event-input'
import { BaseFormProps } from '../../../lib/types/utils'
import { getSubmitAction } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { getSelectedOption } from '../../../lib/utils/functions/misc.functions'
import { FORM_MODE } from '../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../elements/forms/form'
import ControlledInput from '../../elements/inputs/controlled-input'
import ControlledMultiSelectInput from '../../elements/inputs/controlled-multi-select-input'
import ControlledSelectInput from '../../elements/inputs/controlled-select-input'
import ValidationButtons from '../../elements/validation-buttons'
import { CENTER_EVENT_SCHEMA } from './utils'

type Props = {
  variant?: 'default' | 'single-enrollment'
  centerId: number
}

const CenterEventForm = ({
  defaultValues,
  centerId,
  variant = 'default',
  ...validationButtonProps
}: BaseFormProps<CenterEventInput, true> & Props) => {
  const { user } = useAuth()

  const form = useForm<CenterEventInput>({
    resolver: zodResolver(CENTER_EVENT_SCHEMA),
    defaultValues,
    mode: FORM_MODE
  })

  const { data: enrollments } = useQuery({
    queryKey: ['enrollments', { centerId }],
    queryFn: () =>
      getEnrollments({ filter: { centerIds: [centerId] } }, [
        'id',
        'childFirstName',
        'childLastName'
      ])
  })

  const { data: users } = useQuery({
    queryKey: ['users', 'admins'],
    queryFn: () => getUsers({ filter: { roles: [RoleEnum.ADMIN] } })
  })

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}
        className="flex flex-col gap-3 p-4"
      >
        <div className="flex gap-2">
          <Badge.Label
            size="medium"
            label="Evènement créé par : "
            value={toFullName(user?.firstName, user?.lastName)}
          />
          <Badge.Label
            size="medium"
            label="Date/heure : "
            value={DateFormatter.ddMMYYYY_HHMM(new Date())}
          />
        </div>

        {variant === 'default' && (
          <FormField
            control={form.control}
            name={'enrollmentIds'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledMultiSelectInput
                    {...field}
                    label={'Selectionnez le(s) jeune(s)'}
                    variant={'primary'}
                    disabled={enrollments?.data.length === 0}
                    options={
                      enrollments?.data.map((enrollment) => ({
                        label: toFullName(enrollment.childFirstName, enrollment.childLastName),
                        value: enrollment.id
                      })) ?? []
                    }
                    selectedOptions={
                      field.value?.map((enrollmentId) => ({
                        label: toFullName(
                          enrollments?.data.find((enrollment) => enrollment.id === enrollmentId)
                            ?.childFirstName,
                          enrollments?.data.find((enrollment) => enrollment.id === enrollmentId)
                            ?.childLastName
                        ),
                        value: enrollmentId
                      })) ?? []
                    }
                  />
                </FormControl>
              </FormItem>
            )}
          />
        )}
        <FormField
          control={form.control}
          name={'reporter'}
          render={({ field }) => (
            <FormItem className={'flex-1'}>
              <FormControl>
                <ControlledInput
                  {...field}
                  required
                  type={'text'}
                  label={"Personne qui fait part de l'évènement"}
                />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={'description'}
          render={({ field }) => (
            <FormItem className={'flex-1'}>
              <FormControl>
                <ControlledInput {...field} required type={'textarea'} label={'Description'} />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name={'types'}
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormControl>
                <ControlledMultiSelectInput
                  {...field}
                  required
                  label="Type d’évènement"
                  variant="primary"
                  options={Object.values(EventTypeEnum).map((type) => ({
                    label: eventTypeEnumMapping[type].label,
                    value: type
                  }))}
                  selectedOptions={
                    field.value?.map((type) => ({
                      label: eventTypeEnumMapping[type].label,
                      value: type,
                      variant: eventTypeEnumMapping[type].variant
                    })) ?? []
                  }
                />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name={'assignedToId'}
          render={({ field }) => (
            <FormItem className={'flex-1'}>
              <FormControl>
                <ControlledSelectInput
                  {...field}
                  optional
                  label={"Assigner l'évènement"}
                  options={
                    users?.data.map((user) => ({
                      label: toFullName(user.firstName, user.lastName),
                      value: user.id
                    })) ?? []
                  }
                  selectedOption={getSelectedOption(
                    field.value,
                    toFullName(
                      users?.data.find((user) => user.id === field.value)?.firstName,
                      users?.data.find((user) => user.id === field.value)?.lastName
                    )
                  )}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name={'files'}
          render={({ field }) => (
            <FormItem className={'flex-1'}>
              <FormControl>
                <UploadFileInput
                  types={[FileTypeEnum.IMAGE, FileTypeEnum.PDF]}
                  placeholder="Glisser-déposer ou parcourir"
                  placeholderVariant="tag"
                  placeholderColor={tailwindColors.grey.lighter}
                  icon={DownloadFile}
                  hasError={!!form.formState.errors.files}
                  multiple
                  previewOnClick
                  label={'Ajouter une ou plusieurs pièces jointes'}
                  files={field?.value ? field?.value : undefined}
                  onFilesChange={(files) => {
                    field.onChange(files.length === 0 ? null : files)
                  }}
                />
              </FormControl>
            </FormItem>
          )}
        />
        <ValidationButtons {...validationButtonProps} />
      </form>
    </Form>
  )
}

export default CenterEventForm
